/**
 * Typographic systems, resolved to numbers here and nowhere else.
 *
 * The canon pass on Bear was blunt about this: *"a generator emitting
 * free-valued type scales and leading will produce structurally valid layouts
 * that read as templates."* So the spec names a **system**; the runtime owns
 * the numbers. That inversion is the whole defence against generated-looking
 * output, and it only works if this table is the single source of them.
 */

import type { Density, TypeSystem, Palette } from '@morph/spec';
import { MIN_TARGET_PX } from './a11y.js';
import type { IRTokens, ViewportClass } from './ir.js';

/** Modular scales. Measure held to 60–75 characters, leading derived from size. */
const SYSTEMS: Record<TypeSystem, { ratio: number; base: number; measure: number; leading: number }> = {
  editorial: { ratio: 1.25,  base: 17, measure: 68, leading: 1.62 },
  utility:   { ratio: 1.20,  base: 15, measure: 72, leading: 1.50 },
  compact:   { ratio: 1.15,  base: 14, measure: 76, leading: 1.42 },
  display:   { ratio: 1.333, base: 16, measure: 62, leading: 1.35 },
};

/** Discrete, not interpolated. At the dense end metadata is **suppressed**,
 *  not shrunk — a scalar would have produced valid, unreadable layouts. */
const DENSITY: Record<Density, { gap: number; target: number }> = {
  airy:     { gap: 20, target: 44 },
  balanced: { gap: 14, target: 44 },
  dense:    { gap: 8,  target: 44 },   // target never shrinks. See a11y.ts.
};

const VIEWPORT_SCALE: Record<ViewportClass, number> = {
  compact: 0.94, regular: 1, wide: 1,
};

/** The breathing room around an editable value, in em, so it reads as text and
 *  not as a field. It lives here rather than in the stylesheet because the
 *  accessibility floor has to know how tall that box ends up: while the number
 *  was only in the CSS, the edit target resolved to 21px at compact width and
 *  no check in the runtime could see it. */
const EDIT_PAD_EM = 0.06;

export function resolveTokens(
  density: Density, typography: TypeSystem, palette: Palette, viewport: ViewportClass,
): IRTokens {
  const sys = SYSTEMS[typography];
  const d = DENSITY[density];
  const vp = VIEWPORT_SCALE[viewport];
  const base = Math.round(sys.base * vp * 100) / 100;

  return {
    density, typography, palette,
    scale: [0, 1, 2, 3, 4].map((step) => Math.round(base * sys.ratio ** step * 100) / 100),
    measureCh: sys.measure,
    leading: sys.leading,
    gapPx: d.gap,
    // The minimum touch target is a floor, not a preference. Density may not
    // trade it away, which is why it is identical across all three.
    minTargetPx: d.target,
    // Derived from the line box the renderer actually paints — leading plus the
    // padding above and below — and then clamped at the floor rather than
    // trusted to clear it. 'compact' typography at compact width computes to
    // 20.3px, which is a combination the demo's own viewport control reaches.
    editTargetPx: Math.max(
      MIN_TARGET_PX,
      Math.round(base * (sys.leading + 2 * EDIT_PAD_EM) * 100) / 100,
    ),
    editPadPx: Math.round(base * EDIT_PAD_EM * 100) / 100,
  };
}

/**
 * The font behind each typographic system.
 *
 * The spec names a *system*; the runtime owns the numbers, and a family is a
 * number in every way that matters. These are the real families each system
 * implies, with a full fallback stack so a page that never reaches Google Fonts
 * still lands on something with the right voice rather than on Times.
 *
 * Chosen rather than drawn: Joaquín's standing instruction is to use what
 * exists. Inter and Source Serif 4 are open-licensed, hinted, and already the
 * default answer for interface and reading text respectively; drawing our own
 * would be a year of work to arrive somewhere worse.
 */
const FONTS: Record<TypeSystem, string> = {
  // Reading. A serif because the system exists for long-form text, and the
  // measure and leading in SYSTEMS are set for exactly that.
  editorial: "'Source Serif 4', 'Iowan Old Style', Charter, Georgia, 'Times New Roman', serif",
  // Working. The interface default: neutral, wide language coverage, tabular
  // figures for the table presentation.
  utility:   "Inter, 'Inter Variable', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  // Scanning. Same family as utility rather than a narrower one — at compact
  // density the size and leading already do the work, and switching family
  // between densities makes a density change read as a different document.
  compact:   "Inter, 'Inter Variable', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  // Presenting. High-contrast display serif for titles that carry a view.
  display:   "'Instrument Serif', 'Playfair Display', 'Source Serif 4', Georgia, serif",
};

export function fontStackFor(typography: TypeSystem): string {
  return FONTS[typography];
}

/** The families the stacks above want loaded, so a host can request exactly
 *  these and nothing else. Returned rather than hardcoded in the demo's HTML,
 *  because the runtime owns which family a system resolves to. */
export const WEBFONT_FAMILIES = [
  'Inter:wght@400;500;600;700',
  'Source+Serif+4:opsz,wght@8..60,400;8..60,600',
  'Instrument+Serif:ital@0;1',
] as const;
